import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export interface AdminSchool {
  schoolId: string
  schoolName: string
}

interface UseAdminSchoolsReturn {
  schools: AdminSchool[]
  loading: boolean
  error: string | null
  refresh: () => void
}

/**
 * Lists every school the authenticated caller actively administers (OWNER or ADMIN).
 *
 * The underlying data source for SelectedSchoolContext (the shared "which school am I
 * currently managing" selection used across the school-admin portal — Instructors,
 * Students, and the invite form all read the same selection via useSelectedSchool()
 * rather than calling this hook directly). Not itself a trust boundary: every query that
 * uses the resulting school id remains independently scoped by RLS.
 */
export function useAdminSchools(): UseAdminSchoolsReturn {
  const { user } = useAuth()
  const [schools, setSchools] = useState<AdminSchool[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSchools = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError(null)
    try {
      // RLS "memberships: own read" restricts this to the caller's own rows regardless
      // of what is asked for; the explicit filters are defence in depth.
      const { data, error: fetchErr } = await supabase
        .from('school_memberships')
        .select('school_id, schools(name)')
        .eq('user_id', user.id)
        .in('role', ['OWNER', 'ADMIN'])
        .eq('status', 'active')
        .order('joined_at', { ascending: true })

      if (fetchErr) { setError('Could not load your schools.'); return }

      setSchools(
        (data ?? []).map((row: any) => ({
          schoolId: row.school_id,
          schoolName: row.schools?.name ?? 'Unnamed school',
        }))
      )
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => { fetchSchools() }, [fetchSchools])

  return { schools, loading, error, refresh: fetchSchools }
}
